<template>
  <div>
    <div v-if="storeData" class="filter-content">
      <div class="filter-title">{{ title }}</div>
      <div class="filter-content-items">
        <div class="filter-items">
          <template v-for="(filterGroup, index) in filtersGroup">
            <div :key="`${index}${i}`" class="filter-component" v-for="(f, i) in filterGroup.fields">
              <kendo-multiselect v-if="f.type === 'multiselect'" v-model="f.values"
                @change="saveSessionManager(f); updateDataSources(filterGroup, i)" :name="getKey(f)"
                :description="f.description" :dataTextField="f.text" :dataValueField="f.value" :label="f.title"
                :placeholder="!f.noPlaceholder ? f.required ? f.title : getPlaceholder(f.title) : ''"
                :dataSource="f.dataSource" :required="f.required" :listWidth="238" :index="f.index"
                :groupField="f.group">
              </kendo-multiselect>
              <kendo-dropdown v-else-if="f.type === 'dropdown'" v-model="f.values"
                @change="saveSessionManager(f); changeValue(f); updateDataSources(filterGroup, i);" :name="getKey(f)"
                :description="f.description" :dataTextField="f.text" :dataValueField="f.value" :label="f.title"
                :placeholder="!f.noPlaceholder ? f.required ? f.title : getPlaceholder(f.title) : ''"
                :dataSource="f.dataSource" :required="f.required" :listWidth="238" :index="f.index">
              </kendo-dropdown>
              <kendo-slider v-else-if="f.type === 'slider'" v-model="f.values" :label="f.title"
                :description="f.description" :dataSource="f.dataSource" :dataTextField="f.text"
                :dataValueField="f.value" :largeStep="0" :hint="true" :range="!!f.range">
              </kendo-slider>
              <div v-else-if="f.type === 'separator'" class="separator"></div>
            </div>
          </template>
        </div>
        <div class="filter-actions">
          <button id="btn-submit" class="btn btn-sm btn-option btn-disabled" @click="onSubmit">
            <i class="fa fa-check"></i>
          </button>
          <button class="btn btn-link" @click="onClose">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';

import KendoDropdownComponent from '../../../shared/components/kendo-dropdown/kendo-dropdown.component';
import KendoMultiSelectComponent from '../../../shared/components/kendo-multiselect/kendo-multiselect.component';
import KendoSliderComponent from '../../../shared/components/kendo-slider/kendo-slider.component';

import { NOTIFY_TYPE, notify } from '../../../../scripts/shared/notify';

import {
  FILTERS_GET_DATA,
  FILTERS_GET_VALUES,
  FILTERS_UPDATE_VALUES
} from '../../../../store/modules/filters/types';

import Workspace from '../../../../scripts/workspaces';
import { ControlType, Item } from '../../../../scripts/workspaces/item';
import { translate } from '../../../../scripts/core/tools/translate';

const FILTER_OPTION_SELECT_ALL = -9999;

const translateFilter = () => {
  setTimeout(() => {
    const xpath = "//li[text()='Selecionar Todos']";
    const el = document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;

    if (el) {
      el.innerText = translate(el.innerText);
    }
  }, 1000);
};

export default {
  name: 'layer-filters',

  props: {
    id: {
      type: String,
      required: true
    },
    world: {
      type: Object,
      required: true
    },
    title: String
  },

  data() {
    return {
      filtersGroup: undefined,
      filtersSummary: '',
      storeValues: undefined
    };
  },

  computed: {
    ...mapGetters({
      storeData: FILTERS_GET_DATA
    })
  },

  methods: {
    ...mapActions({
      updateFiltersValues: FILTERS_UPDATE_VALUES
    }),

    isValid() {
      return _.reduce(this.filtersGroup, (res, filters) => {
        _.forEach(filters.fields, f => {
          if (f.required && (!_.size(f.values) || !this.hasValue(f.values.value))) {
            notify(NOTIFY_TYPE.LOG, `<strong>${f.title}</strong> é necessário.`);
            res = false;
          }
        });
        return res;
      }, true);
    },

    getKey(field) {
      return (field.name || _.snakeCase(_.deburr(field.title)));
    },

    getPlaceholder(text, suffix) {
      return _.some(suffix, t => text.includes(t, text.length - 3)) ? `${translate('Common.All_1')}...` : `${translate('Common.All')}...`;
    },

    hasValue(value) {
      return (_.isArray(value) ? _.size(value) : _.isNumber(value) ? !!value : !!String(value));
    },

    createFiltersGroup(filters) {
      return _(_.cloneDeep(filters))
        .map(f => {
          f.fields = _.filter(f.fields, (field) => {
            if (_.size(field.visible)) {
              return _.some(field.visible, v => String(v) === String(this.id));
            } else if (_.size(field.hidden)) {
              return !_.some(field.hidden, v => String(v) === String(this.id));
            }
            return true;
          });
          return f;
        })
        .orderBy(f => f.position)
        .value();
    },

    populateFiltersGroup() {
      _.forEach(this.filtersGroup, (filterGroup) => {
        _.forEach(filterGroup.fields, (field) => {
          if (!field.dataSource) {
            field.dataSource = this.formatDataSource(filterGroup.data, field);
          }

          try {
            const values = this.storeValues[this.id][this.world._id][this.getKey(field)];
            field.values = values.metadata.all ? {} : values;
          } catch (err) {
            field.values = {};
          }
        });
      });
    },

    formatDataSource(dataSource, field) {
      if (_.size(dataSource)) {
        return _(dataSource)
          .uniqBy(field.value)
          .orderBy(d => d[field[field.orderBy || 'text']])
          .value();
      }
      return [];
    },

    filterDataSource(dataSource, options) {
      return _.filter(dataSource, (d) => {
        return _.every(options, option => {
          if (_.isArray(option.value)) {
            if (_.size(option.value)) {
              return _.some(option.value, value => String(value) === String(d[option.metadata.value]));
            }
            return true;
          } else if (String(options.value)) {
            return String(d[option.metadata.value]) === String(option.value);
          }
          return true;
        });
      });
    },

    createFilteredDataSource(filterGroup, field, options) {
      const dataSource = _.find(this.storeData, d => String(d.name) === String(filterGroup.name)).data;
      return this.formatDataSource(this.filterDataSource(dataSource, options), field);
    },

    isValidFilterProperty(filterGroup, field, val, key) {
      if (val.metadata && val.metadata.name) {
        return _.some(filterGroup.fields, f => this.getKey(f) === val.metadata.name) && (this.getKey(field) !== key);
      }
      return false;
    },

    updateDataSources(filterGroup, index) {
      const values = this.getFiltersValues(false);
      if (values.type || values.group || values.segment || values.state || values.models || values.competingClassification || values.brands || values.contexts || values.storeTypeSaleMarket || values.attributes) {
        $('.filter-actions #btn-submit').removeClass('btn-disabled');
      } else {
        $('.filter-actions #btn-submit').addClass('btn-disabled');
      }

      _(this.filtersGroup)
        .filter(fg => String(fg.name) === String(filterGroup.name))
        .forEach((fg) => {
          _(fg.fields)
            .filter((fd, i) => {
              if (i > index || (fd.values && fd.values.value && Array.isArray(fd.values.value) && fd.values.value.some(x => String(x) === String(FILTER_OPTION_SELECT_ALL)))) {
                delete values[this.getKey(fd)];
                return true;
              }
              return false;
            })
            .forEach((fd) => {
              if (fd.values && fd.values.value && fd.values.value.some(x => String(x) === String(FILTER_OPTION_SELECT_ALL))) {

                const valueField = fd.value || 'value';
                const textField = fd.text || 'text';

                const dataSource = fd.dataSource.filter(x => x[valueField] !== FILTER_OPTION_SELECT_ALL);

                fd.values.value = dataSource.map(x => x[valueField]);
                fd.values.text = dataSource.map(x => x[textField]);

                return;
              }

              const filteredValues = _.reduce(values, (res, v, k) => {
                if (this.isValidFilterProperty(fg, fd, v, k)) {
                  res[k] = v;
                }
                return res;
              }, {});

              fd.dataSource = this.createFilteredDataSource(fg, fd, filteredValues);
              if (_.size(_.keys(fd.values))) {
                fd.values.value = _.isArray(fd.values.value) ? _.filter(fd.values.value, v => _.some(fd.dataSource, d => _.isEqual(d[fd.value], v))) : _.some(fd.dataSource, d => _.isEqual(d[fd.value], fd.values.value)) ? fd.values.value : '';
                fd.values.text = _.isArray(fd.values.text) ? _.filter(fd.values.text, t => _.some(fd.dataSource, d => _.isEqual(d[fd.text], t))) : _.some(fd.dataSource, d => _.isEqual(d[fd.text], fd.values.text)) ? fd.values.text : '';
              }
            });
        });

      this.filtersGroup = _.assign({}, this.filtersGroup);
    },

    saveSessionManager(field) {
      const controls = {
        multiselect: () => Workspace.addAction(new Item(`#filter-container input[name=${this.getKey(field)}]`, field.values.value, ControlType.KendoMultiselect)),
        dropdown: () => Workspace.addAction(new Item(`#filter-container input[name=${this.getKey(field)}]`, field.values.value, ControlType.KendoDropDownList))
      };

      controls[field.type]();
    },

    createValueFromField(f) {
      return {
        metadata: {
          name: this.getKey(f),
          text: f.text,
          value: f.value,
          properties: Object.keys(f.dataSource[0] || {}),
          total: f.dataSource.length,
          all: true
        },
        ..._.reduce(f.dataSource, (acc, d) => {
          acc.text.push(d[f.text]);
          acc.value.push(d[f.value]);
          return acc;
        }, { text: [], value: [] })
      };
    },

    getFiltersValues(all = true) {
      return _.reduce(this.filtersGroup, (res, filters) => {
        _(filters.fields)
          .forEach(f => {
            const values = f.values || {};
            if (this.hasValue(values.value)) {
              res[this.getKey(f)] = values;
            } else if (all && (!f.required && !this.hasValue(values.value)) && !f.skipAll) {
              res[this.getKey(f)] = this.createValueFromField(f);
            }
          });
        return res;
      }, {});
    },

    onSubmit() {
      if (this.isValid()) {
        const values = this.getFiltersValues();

        this.filtersSummary = _(values)
          .filter(f => (f.metadata ? !f.metadata.all : true) && _.size(f.text))
          .map(f => _.isArray(f.text) ? f.text.join(', ') : f.text)
          .value()
          .join(' > ');

        this.updateFiltersValues({ id: this.id, worldId: this.world._id, values });

        $('.filter-actions #btn-submit').addClass('btn-disabled');
        this.$emit('submit', values);
      }
    },

    changeValue(field) {
      const selectedValue = field.values.value;

      this.$emit('dropdownChanged', {
        fieldName: this.getKey(field),
        value: selectedValue
      });

    },

    onClose() {
      this.$emit('close');
    }
  },

  mounted() {
    this.storeValues = this.$store.getters[FILTERS_GET_VALUES];
    this.filtersGroup = this.createFiltersGroup(this.storeData);
    this.populateFiltersGroup();
    translateFilter();
  },

  watch: {
    storeData(val) {
      this.filtersGroup = this.createFiltersGroup(val);
      this.populateFiltersGroup();
      translateFilter();
    }
  },

  components: {
    'kendo-dropdown': KendoDropdownComponent,
    'kendo-multiselect': KendoMultiSelectComponent,
    'kendo-slider': KendoSliderComponent
  }
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.separator {
  width: 1px;
  height: 57px;
  background-color: #ddd;
  margin: -10px 0;
  box-shadow: -1px 0px 0px rgba(255, 255, 255, 0.5);
}
</style>



// WEBPACK FOOTER //
// src/modules/filters/containers/layer-filters/layer-filters.container.vue